import {
    seeProjectPrice,
    userCanSeeBestPrice,
    userCanSeeBestPriceOnProductCard
} from '@ab-core-functions/user-permission';
import { useBestPrices, useProductsAvailabilitiesWithChannel } from '@ab-core/hooks-v2';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import type { MappedLineItem } from '@ab-core/hooks/shopping/lineItemMapper';
import type { DefaultScalableProductCardProps } from '../cardLayout';
import { SPECIAL_SUPPLIER_IDS } from './';
import { fallBackPosition, fallBackQuantityUnit, fallBackSalesUnit } from './fallBackStates';
import { isSpecialSupplierProduct } from './utils';

type LineItemProduct = MappedLineItem;

export const useMappedLineItemToScalableProduct = (
    products?: Array<LineItemProduct>
): Array<DefaultScalableProductCardProps['product']> => {
    const { activeCart } = getActiveCart();
    const hasBestPrice = userCanSeeBestPrice();
    const hasBestPriceOnProductCard = userCanSeeBestPriceOnProductCard();
    const skus: Array<string> = [];
    let projectNumber =
        activeCart?.custom.customFieldsRaw.find((element) => element.name === 'cart-projectNr')?.value || '0';
    const canSeeProjectPrice = seeProjectPrice();
    projectNumber = canSeeProjectPrice ? projectNumber : '0';
    products?.map((product) => {
        if (product?.sku) {
            skus.push(product.sku);
        }
    });

    const { bestPrices } = useBestPrices(skus, (!hasBestPrice || !hasBestPriceOnProductCard) && skus.length === 0);
    const { productsAvailabilitiesWithChannel } = useProductsAvailabilitiesWithChannel(
        {
            skus,
            cartId: activeCart?.id
        },
        !activeCart
    );

    const productList: Array<DefaultScalableProductCardProps['product']> = [];
    products?.map((product) => {
        const productAvailability = productsAvailabilitiesWithChannel?.find(
            (available) => available.sku === product.sku
        );

        const isItShop = isSpecialSupplierProduct(
            SPECIAL_SUPPLIER_IDS.AVM,
            product.supplierId,
            productAvailability?.aggregatedMainChannelQuantity
        );

        productList.push({
            lineItemId: product.id,
            defaultQuantity: product.quantity || product.salesUnit || fallBackSalesUnit,
            defaultPositionText: product.positionText || '',
            sku: product.sku,
            information: { reach: product?.reach || false },
            tags: {
                product: {
                    isAbakus: product.isAbakus,
                    isAbakusPlus: product.isAbakusPlus,
                    topProduct: product.topProduct,
                    isTecselect: product.isTecselect,
                    isPromotion: product.promoProduct,
                    scoreCardIndex: product.scoreCardIndex,
                    ean: product.ean,
                    isItShop
                }
            },
            ids: {
                articleNumber: product.sku,
                ean: product.ean,
                customerArticleNumber: product.customerArticleNumber
            },
            image: {
                imgSrc: product.image || '',
                sku: product.sku,
                position: Number(product.position) || fallBackPosition
            },
            name: {
                text: product.productName || product.name,
                sku: product.sku,
                position: Number(product.position) || fallBackPosition
            },
            additionalLinks: {
                sku: product.sku,
                hasAccessories: product.hasAccessories,
                hasAlternatives: product.hasAlternatives
            },
            prices: {
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit
            },
            availability: {
                availability: productsAvailabilitiesWithChannel?.find((available) => available.sku === product.sku),
                quantityUnit: product.quantityUnit || fallBackQuantityUnit
            },
            order: {
                bestPrice: bestPrices?.find((bestPrice) => bestPrice.sku === product.sku),
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit,
                sku: product.sku,
                isUnqualifiedContractProduct: product.isUnqualifiedContractProduct,
                chargeNr: product.chargeNr,
                packagingSize: product?.packagingSize || '',
                position: Number(product.position) || fallBackPosition,
                cartId: activeCart?.id,
                supplierId: product.supplierId,
                stockQuantity:
                    productsAvailabilitiesWithChannel?.find((available) => available.sku === product.sku)
                        ?.aggregatedMainChannelQuantity ?? 0,
                ean: product.ean,
                projectNumber
            },
            comparison: {
                sku: product.sku,
                title: product.productName,
                imageSrc: product.image || ''
            }
        });
    });

    return productList;
};
